import React from 'react';
import memoize from 'memoize-one';
import { css, styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { type AISnippetRendererProps, type SnippetRendererProps } from '@atlaskit/link-provider';

import { isFedRamp } from '@atlassian/atl-context';
import { AISnippetWithRelayEnvironment } from '@atlassian/ai-snippet';

import { LazyEmojiComponentLoader } from '@confluence/emoji-title';
import { fg } from '@confluence/feature-gating';
import { SessionData, SessionDataContext, useSessionData } from '@confluence/session-data';
import { expVal } from '@confluence/feature-experiments';

const emojiWrapperStyles = css({
	display: 'inline-block',
	font: token('font.body.small'),
	justifyContent: 'center',
	overflow: 'hidden',
	margin: 'auto',
	minWidth: '16px',
});

const SmartCardEmojiWrapper: React.ComponentType<React.HTMLAttributes<HTMLSpanElement>> = ({
	children,
}) => <span css={emojiWrapperStyles}>{children}</span>;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiWrapper = styled.span({
	justifyContent: 'center',
	overflow: 'hidden',
	display: 'inline-block',
	margin: 'auto',
	minWidth: '16px',
	font: token('font.body.large'),
});

// Custom render wrapper for AISnippet providing session data
const AISnippetRenderer = (props: SnippetRendererProps) => {
	const { displayName, userId, isAdminHubAIEnabled } = useSessionData();

	if (
		!isAdminHubAIEnabled ||
		isFedRamp() ||
		!expVal('confluence-ai-linking-ai-snippets', 'should_render_ai_snippet', false)
	) {
		return props.fallbackComponent;
	}

	return (
		<AISnippetWithRelayEnvironment
			{...(props as AISnippetRendererProps)}
			userId={userId ?? undefined}
			displayName={displayName ?? undefined}
		/>
	);
};

export const getSmartCardRenderers = memoize(() => ({
	renderers: {
		emoji: (emojiId, _parentComponent = 'SmartCard', contentType = 'page') => (
			<SessionData>
				{(sessionData) => (
					<SessionDataContext.Provider value={sessionData}>
						<LazyEmojiComponentLoader
							emoji={emojiId}
							height={16}
							context="smartCard"
							wrapper={
								fg('platform-linking-visual-refresh-v1') ? SmartCardEmojiWrapper : EmojiWrapper
							}
							contentType={contentType}
						/>
					</SessionDataContext.Provider>
				)}
			</SessionData>
		),
		snippet: fg('cc-ai-snippet-renderer-gate') ? AISnippetRenderer : undefined,
	},
}));
